import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Edit, Save, X, Trash2, Image, Link2, Eye } from "lucide-react"
import { ShareableContent } from "@/pages/parts/ShareableContent"
import type { PreviewData } from "@/lib/types"
import { useState } from "react"
import { SubPreviewSelectionModal } from "./SubPreviewSelectionModal"
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogDescription } from "@/components/ui/dialog"
import { getAssetPublicUrl } from "@/utils/image-utils"
import { ProjectSelector } from "@/components/project/ProjectSelector"
import { TagInput } from "@/components/ui/tag-input"
import { TagDisplay } from "@/components/ui/tag-display"
import { useTranslation } from "react-i18next"

interface PreviewDetailsProps {
  preview: PreviewData
  isEditing: boolean
  setIsEditing: (editing: boolean) => void
  editName: string
  setEditName: (name: string) => void
  editShareable: boolean
  setEditShareable: (shareable: boolean) => void
  editSubPreviewId: string | null
  editProjectId: string | null
  setEditProjectId: (projectId: string | null) => void
  editTags: string[]
  setEditTags: (tags: string[]) => void
  isSaving: boolean
  handleSaveChanges: () => void
  handleCancelEdit: () => void
  openDeleteDialog: () => void
  openUpdateImageModal: () => void
  isUpdatingImage: boolean
  formatDate: (dateString: string) => string
  compatible_sub_previews: any[]
  handleSubPreviewChange: (subPreviewId: string | null) => void
  selectedSubPreview: PreviewData | null
}

export function PreviewDetails({
  preview,
  isEditing,
  setIsEditing,
  editName,
  setEditName,
  editShareable,
  setEditShareable,
  editSubPreviewId,
  editProjectId,
  setEditProjectId,
  editTags,
  setEditTags,
  isSaving,
  handleSaveChanges,
  handleCancelEdit,
  openDeleteDialog,
  openUpdateImageModal,
  isUpdatingImage,
  compatible_sub_previews,
  handleSubPreviewChange,
  selectedSubPreview,
}: PreviewDetailsProps) {
  const [isSubPreviewModalOpen, setIsSubPreviewModalOpen] = useState(false)
  const [isImageModalOpen, setIsImageModalOpen] = useState(false)
  const {t} = useTranslation("common")


  const getShareableLink = () => {
    return `${window.location.origin}/shared/${preview.id}`
  }


  // Modified to use direct database update for sub-preview changes
  const handleSubPreviewSelect = (subPreviewId: string | null) => {
    // Only make the API call if the value is actually changing
    if (subPreviewId !== preview.sub_preview_id) {
      handleSubPreviewChange(subPreviewId);
    }
    setIsSubPreviewModalOpen(false);
  };

  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="min-w-0 flex-1 pr-2">
          <CardTitle className="text-xl break-words">{isEditing ? t("common:previewViewer.details.card.edit") : preview.name}</CardTitle>
          <CardDescription>
            {isEditing ? t("common:previewViewer.details.card.isEditing") : t("common:previewViewer.details.card.isNotEditing")}
          </CardDescription>
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={() => (isEditing ? handleCancelEdit() : setIsEditing(true))}
          className="flex-shrink-0"
        >
          {isEditing ? <X className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div>
            <div className="flex items-center justify-between text-sm font-medium text-muted-foreground mb-2">
              <span>{t("common:previewViewer.header.shareable")}</span>
              {isEditing ? (
                <Switch checked={editShareable} onCheckedChange={setEditShareable} />
              ) : (
                <span className="text-sm font-medium">{preview.is_sharable ? t("common:previewViewer.details.card.yes") : t("common:previewViewer.details.card.no")}</span>
              )}
            </div>

            {(preview.is_sharable || isEditing) && (
              <ShareableContent
                shareableLink={getShareableLink()}
                isEditing={isEditing}
                editShareable={editShareable}
              />
            )}
          </div>

          <div>
            <div className="text-sm font-medium text-muted-foreground my-2 flex items-center justify-start">
              <span>{t("common:previewViewer.details.card.labelImage")}</span>
              <Button
                variant="link"
                onClick={() => setIsImageModalOpen(true)}
                className="ml-2"
              >
                <Eye className="w-4 h-4" />
              </Button>
            </div>
            {isEditing && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={openUpdateImageModal}
                  disabled={isUpdatingImage}
                >
                  <Image className="w-4 h-4 mr-2" />
                  {isUpdatingImage ? t("common:previewViewer.details.card.isUpdatingImage") : t("common:previewViewer.details.card.updateImage")}
                </Button>
              </div>
            )}
          </div>

          {isEditing && (
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-2">{t("common:previewViewer.details.card.name")}</div>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="mb-1" />
            </div>
          )}          {/* Project Selection */}
          {isEditing && (
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-2">{t("common:previewViewer.details.card.project")}</div>
              <ProjectSelector
                selectedProjectId={editProjectId}
                onProjectSelect={setEditProjectId}
                showCreateButton={true}
                label=""
              />
            </div>
          )}


          {/* Linked Preview section (renamed from Sub-preview) */}
          {compatible_sub_previews.length > 0 && (
            <div>
              <div className="flex items-center justify-between text-sm font-medium text-muted-foreground mb-2">
                <span>{t("common:previewViewer.details.card.linkedPreview")}</span>
                {isEditing && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsSubPreviewModalOpen(true)}
                  >
                    {t("common:previewViewer.details.card.change")}
                  </Button>
                )}
              </div>

              {/* Show current selection */}
              {editSubPreviewId ? (
                <div className="flex items-center p-2 border rounded-md">
                  <Link2 className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="text-sm truncate">
                    {selectedSubPreview?.name || t("common:previewViewer.details.card.unknownPreview")}
                  </span>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  {t("common:previewViewer.details.card.noLinkedPreview")}
                </div>
              )}
            </div>
          )}

          {/* Tags Section */}
          <div>
            <div className="text-sm font-medium text-muted-foreground mb-2">{t("common:previewViewer.details.card.tags")}</div>
            {isEditing ? (
              <TagInput
                tags={editTags}
                onTagsChange={setEditTags}
                placeholder={t("common:previewViewer.details.card.addTags")}
                maxTags={10}
              />
            ) : (
              <div className="min-h-[2.5rem] flex items-center">
                {preview.tags && preview.tags.length > 0 ? (
                  <TagDisplay
                    tags={preview.tags}
                    maxDisplay={5}
                    showCount={true}
                  />
                ) : (
                  <span className="text-sm text-muted-foreground">{t("common:previewViewer.details.card.noTags")}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex flex-col space-y-3">
        {isEditing && (
          <>
            <Button onClick={handleSaveChanges} className="w-full" disabled={isSaving}>
              {isSaving ? t("common:previewViewer.details.card.isSaving") : (
                <>
                  <Save className="w-4 h-4 mr-2" /> {t("common:previewViewer.details.card.save")}
                </>
              )}
            </Button>
            <Button variant="destructive" className="w-full" onClick={openDeleteDialog}>
              <Trash2 className="w-4 h-4 mr-2" /> {t("common:previewViewer.details.card.delete")}
            </Button>
          </>
        )}
      </CardFooter>

      {/* Linked Preview selection modal (renamed from Sub-preview) */}
      <SubPreviewSelectionModal
        isOpen={isSubPreviewModalOpen}
        onOpenChange={setIsSubPreviewModalOpen}
        compatiblePreviews={compatible_sub_previews}
        preview={preview}
        editSubPreviewId={editSubPreviewId}
        onSelect={handleSubPreviewSelect}
        isLoading={isSaving}
      />

      {/* Image Preview Modal */}
      <Dialog open={isImageModalOpen} onOpenChange={setIsImageModalOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{t("common:previewViewer.details.card.usedLabelImage")}</DialogTitle>
            <DialogDescription></DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-4">
            {preview.asset_path ? (
              <img
                src={getAssetPublicUrl(preview.asset_path)}
                alt={preview.name}
                className="max-w-full max-h-[60vh] object-contain rounded-md"
              />
            ) : (
              <div className="text-center text-muted-foreground py-8">
                {t("common:previewViewer.details.card.noImage")}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
